import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_URL, ENDPOINTS } from '../constants/api';
import { notification } from 'antd';
import AutoCompleteInput from './AutoCompleteInput';
import * as XLSX from 'xlsx';
import { submitRow } from '../utils/ktcUtils';
import { useNavigate } from 'react-router-dom';

const DataEntryTable = ({
  data,
  link,
  token,
}) => {
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const [columns, setColumns] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [search, setSearch] = useState('');

  /* ===================== INIT DATA + UID ===================== */
  useEffect(() => {
    if (!data || data.length === 0) return;

    const withUid = data.map((row, i) => ({
      ...row,
      __row_uid: row.id ?? `${Date.now()}-${i}`,
    }));

    setFilteredData(withUid);
    setOriginalData(withUid);
  }, [data]);

  /* ===================== INIT COLUMNS + DROPDOWNS ===================== */
  useEffect(() => {
    if (!data || data.length === 0) return;

    const cols = Object.keys(data[0]).map((col) => {
      const parts = col.split('~');
      return {
        columnName: col,
        header: parts[0],
        key: parts[1] || parts[0],
        type: parts[2],
        inputId: parts[3],
        visible: parts[2] !== 'hide',
        isDropdown: parts[2]?.includes('dropdown'),
        editable: parts[2]?.includes('editable') || parts[2] === 'api_url',
        dropdownOptions: [],
      };
    });

    setColumns(cols);

    const fetchDropdowns = async () => {
      let updatedCols = [...cols];

      for (let col of updatedCols) {
        if (col.isDropdown && col.inputId) {
          try {
            const res = await axios.get(
              `${API_URL}/${ENDPOINTS.SYS}/get-dropdown/${col.inputId}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            col.dropdownOptions = res.data || [];
          } catch {}
        }
      }

      setColumns(updatedCols);

      // ✅ AUTO-FILL DROPDOWN DEFAULTS INTO STATE
      setFilteredData(prev =>
        prev.map(row => {
          const r = { ...row };
          updatedCols.forEach(col => {
            if (
              col.isDropdown &&
              (!r[col.columnName] || r[col.columnName] === '') &&
              col.dropdownOptions.length > 0
            ) {
              r[col.columnName] = col.dropdownOptions[0].id;
            }
          });
          return r;
        })
      );

      setOriginalData(prev =>
        prev.map(row => {
          const r = { ...row };
          updatedCols.forEach(col => {
            if (
              col.isDropdown &&
              (!r[col.columnName] || r[col.columnName] === '') &&
              col.dropdownOptions.length > 0
            ) {
              r[col.columnName] = col.dropdownOptions[0].id;
            }
          });
          return r;
        })
      );
    };

    fetchDropdowns();
  }, [data, token]);

  /* ===================== SEARCH ===================== */
  const handleSearch = (e) => {
    const v = e.target.value;
    setSearch(v);
    setFilteredData(
      originalData.filter(r =>
        Object.values(r).some(x =>
          String(x).toLowerCase().includes(v.toLowerCase())
        )
      )
    );
  };

  /* ===================== EDIT ===================== */
  const handleEdit = (e, row, col) => {
    const value = e.target?.value ?? e.target?.textContent;

    setFilteredData(prev =>
      prev.map(r =>
        r.__row_uid === row.__row_uid
          ? { ...r, [col.columnName]: value }
          : r
      )
    );

    setOriginalData(prev =>
      prev.map(r =>
        r.__row_uid === row.__row_uid
          ? { ...r, [col.columnName]: value }
          : r
      )
    );
  };

  /* ===================== SUBMIT ALL ===================== */
  const handleSubmitAll = async () => {
    if (selectedRows.size === 0) {
      notification.warning({ message: 'No rows selected' });
      return;
    }

    const rowsToSubmit = filteredData.filter(r =>
      selectedRows.has(r.__row_uid)
    );

    const results = await Promise.all(
      rowsToSubmit.map(row => submitRow(row, null, token))
    );

    const success = results.filter(r => r.success).length;
    const failed = results.length - success;

    notification.info({
      message: 'Submit Result',
      description: `Success: ${success}, Failed: ${failed}`,
    });

    navigate(-1);
  };

  /* ===================== EXPORT ===================== */
  const exportToExcel = (rows, title) => {
    const clean = rows.map(r => {
      const o = {};
      Object.keys(r).forEach(k => {
        if (!k.startsWith('__')) o[k.split('~')[0]] = r[k];
      });
      return o;
    });
    const ws = XLSX.utils.json_to_sheet(clean);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'data');
    XLSX.writeFile(wb, `${title}.xlsx`);
  };

  /* ===================== RENDER ===================== */
  return (
    <div>
      <div className="row mb-3">
        <div className="col-9">
          <button
            className="btn btn-primary me-2"
            onClick={() => exportToExcel(filteredData, link.page_title)}
          >
            Export Excel
          </button>
        </div>
        <div className="col-3">
          <input
            ref={inputRef}
            value={search}
            onChange={handleSearch}
            className="form-control"
            placeholder="Search..."
          />
        </div>
      </div>

      <table className="table table-bordered">
        <thead className="table-dark">
          <tr>
            <th>
              <input
                type="checkbox"
                checked={selectedRows.size === filteredData.length}
                onChange={(e) =>
                  setSelectedRows(
                    e.target.checked
                      ? new Set(filteredData.map(r => r.__row_uid))
                      : new Set()
                  )
                }
              />
            </th>
            {columns.filter(c => c.visible).map(c => (
              <th key={c.key}>{c.header}</th>
            ))}
          </tr>
        </thead>

        <tbody>
          {filteredData.map(row => (
            <tr key={row.__row_uid}>
              <td>
                <input
                  type="checkbox"
                  checked={selectedRows.has(row.__row_uid)}
                  onChange={() =>
                    setSelectedRows(prev => {
                      const s = new Set(prev);
                      s.has(row.__row_uid)
                        ? s.delete(row.__row_uid)
                        : s.add(row.__row_uid);
                      return s;
                    })
                  }
                />
              </td>

              {columns.filter(c => c.visible).map(col => (
                <td key={col.key}>
                  {col.isDropdown ? (
                    <select
                      className="form-select form-select-sm"
                      value={row[col.columnName] ?? ''}
                      onChange={(e) => handleEdit(e, row, col)}
                    >
                      {col.dropdownOptions.map(o => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                  ) : col.editable ? (
                    <div
                      contentEditable
                      suppressContentEditableWarning
                      onBlur={(e) => handleEdit(e, row, col)}
                    >
                      {row[col.columnName]}
                    </div>
                  ) : (
                    row[col.columnName]
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="text-end mt-3">
        <button className="btn btn-success" onClick={handleSubmitAll}>
          Save Selected – {selectedRows.size}
        </button>
      </div>
    </div>
  );
};

export default DataEntryTable;
